import React from "react";
import Dropdown, { IOption } from "src/components/Dropdown/Dropdown";
import { getPaginationItems } from "./paginationItems";
import styles from "./Pagination.module.scss";

interface IProps {
  page: number;
  pageSize: number;
  total: number;
  pageSizeOptions?: number[];
  itemLabel?: string;
  onPageChange(page: number): void;
  onPageSizeChange(pageSize: number): void;
  children?: React.ReactNode;
}

const Pagination: React.FC<IProps> = ({
  page,
  pageSize,
  total,
  pageSizeOptions = [5, 10, 20],
  itemLabel = "entries",
  onPageChange,
  onPageSizeChange,
  children,
}) => {
  const totalPages = Math.max(1, Math.ceil(total / pageSize));
  const safePage = Math.min(Math.max(page, 1), totalPages);
  const start = total === 0 ? 0 : (safePage - 1) * pageSize + 1;
  const end = total === 0 ? 0 : Math.min(safePage * pageSize, total);
  const items = getPaginationItems(safePage, total === 0 ? 0 : totalPages);
  const sizeOptions: IOption[] = pageSizeOptions.map((size) => ({
    id: String(size),
    value: String(size),
  }));
  const sizeValue = sizeOptions.find((option) => option.id === String(pageSize)) || sizeOptions[0];

  return (
    <div className={styles.pagination}>
      <div className={styles.summary}>
        <p className={styles.count}>
          Showing <strong>{start}–{end}</strong> of <strong>{total}</strong> {itemLabel}
        </p>
        <div className={styles.size}>
          <Dropdown
            label="Per page"
            placeholder={sizeValue.value}
            options={sizeOptions}
            value={sizeValue}
            alignRight
            onChange={(_event, option) => onPageSizeChange(Number(option.id))}
          />
        </div>
      </div>
      {children}
      <div className={styles.pager}>
        <button
          type="button"
          className={styles.step}
          disabled={safePage <= 1 || total === 0}
          onClick={() => onPageChange(safePage - 1)}
        >
          Previous
        </button>
        <div className={styles.pages}>
          {items.map((item, index) => item === "ellipsis" ? (
            <span key={`ellipsis-${index}`} className={styles.ellipsis}>…</span>
          ) : (
            <button
              key={item}
              type="button"
              className={item === safePage ? styles.pageActive : styles.page}
              onClick={() => onPageChange(item)}
            >
              {item}
            </button>
          ))}
        </div>
        <span className={styles.mobileLabel}>Page {total === 0 ? 0 : safePage} of {total === 0 ? 0 : totalPages}</span>
        <button
          type="button"
          className={styles.step}
          disabled={safePage >= totalPages || total === 0}
          onClick={() => onPageChange(safePage + 1)}
        >
          Next
        </button>
      </div>
    </div>
  );
};

export default Pagination;
